import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Table, Alert, Pagination } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { getDataOfType } from 'src/lib/utils/get-data-of-type'
import B2BLayout from 'src/react-app/layouts/b2b'
import fetchAPI from 'src/lib/utils/fetch-api'
import {afterDataColumns} from 'src/react-app/components/list-operations/consts'
import { getStagesGroup } from 'src/react-app/pages/list-orders/utils'
import { calcTotalWidth, tableRowClick, fetchSchema, formatListData } from 'src/lib/utils/list'
import { TableWrapper, PaginationWrapper, TableHeaderWrapper } from './styles'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import OrderFilter from './orders-filter'
import './index.css'
import EditCard from '../../components/list-operations/edit-card'
import StageCell from '../../components/list-operations/stage-cell'
import { CountBallInline } from '../../components/activity-timeline/styles'

const name = 'appeal'

const createSession = createSelector(
    (state: any) => state.session,
    sessionData => sessionData
)

const createViewed = createSelector(
  (state: any) => state.viewed,
  counts => counts
)

function formatListId(data) {
  const parentId = Number(data['parent-id'])
  let result = data['id']

  if (parentId) {
    result = parentId
  }

  return result
}

function getFontWeightId(data) {
  const isStageNew = getDataOfType(data, 'stage', Number, null) === 0
  return isStageNew ? 'bold' : ''
}

const beforeDataColumns = [
  {
    title: 'ID',
    width: 70,
    dataIndex: 'id',
    key: 'id',
    fixed: 'left',
    render: (_, data) => {
      return (
        <EditCard
          fontWeight={getFontWeightId(data)}
          id={formatListId(data)}
          schema={data?.schema}
          title="Редактирование"
        >{formatListId(data)}</EditCard>
      )
    },
  },
  {
    title: 'Стадия',
    width: 190,
    key: 'stage',
    render: (_, data) => {
      const { stage, stages } = data
      return (<StageCell stage={stage} stages={stages}/>)
    }
  }
]

export default function AdminObjectsListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const orderFiltersRef = useRef<any>({})
  const [dataSource, setDataSource] = useState<any[]>([])
  const [columns, setColumns] = useState<any[]>([])
  const [tableWidth, setTableWidth] = useState<number>()
  const [totalRows, setTotalRows] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const { counts } = useSelector(createViewed)
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [pageErrors, setPageErrors] = useState<String[]>([])

  const breadcrumbs = useMemo(() => (
    [{
      href: '/cases',
      title: 'Контактный центр',
    }, {
      href: location.pathname,
      title: 'Обращения',
    }]
  ), [location.pathname])
  const { sessionData } = useSelector(createSession)

  const addDataColumns = useMemo(() => {
    const addDataColumns: any[] = [
      {
        title: 'Дата обращения',
        dataIndex: 'case-date',
        key: 'case-date',
        width: 120,
        sorter: true
      },
      {
        title: 'ФИО менеджера',
        dataIndex: 'case-manager',
        key: 'case-manager',
        width: 120,
        sorter: true,
      },
      {
        title: 'Контактное лицо клиента',
        dataIndex: 'name',
        key: 'name',
        width: 120
      },
      {
        title: 'Телефон клиента',
        dataIndex: 'telephone',
        key: 'telephone',
        width: 120
      },
      {
        title: 'Мессенджер',
        dataIndex: 'messenger',
        key: 'messenger',
        width: 120
      },
      {
        title: 'Регион',
        dataIndex: 'region',
        key: 'region',
        width: 120,
        sorter: true
      },
      {
        title: 'Город',
        dataIndex: 'city',
        key: 'city',
        width: 120,
        sorter: true
      },
      {
        title: 'Адрес объекта',
        dataIndex: 'address-object',
        key: 'address-object',
        width: 120
      },
      {
        title: 'Вид контакта',
        dataIndex: 'source-appeal',
        key: 'source-appeal',
        width: 120
      },
      {
        title: 'Сумма заказа',
        dataIndex: 'order-price',
        key: 'order-price',
        width: 130,
        sorter: true,
        render: (key) => (key || 0).toLocaleString('ru-RU', {style: 'currency', currency: 'RUB', currencyDisplay: 'symbol'}).replace("₽", "")
      },
      {
        title: 'Номер производственного заказа',
        dataIndex: 'production-order-number',
        key: 'production-order-number',
        width: 120,
        sorter: true
      },
      {
        title: 'В работе менеджера с',
        dataIndex: 'sales-manager-work-time',
        key: 'sales-manager-work-time',
        width: 120,
        sorter: true
      },
      {
        title: 'Причина отмены',
        width: 200,
        dataIndex: 'workflow-id',
        key: 'workflow-id',
      },
    ]

    const chatColumns = {
      title: 'Чаты',
      width: 60,
      dataIndex: 'unread_messages',
      key: 'unread_messages',
      sorter: true,
      sortOrder: orderFiltersRef.current.field === 'chat_count' ? orderFiltersRef.current.order : null,
      render: (_, data) => {
        const { unread_messages } = data
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {unread_messages ? (<CountBallInline title={'unread_messages'} bgColor={'countColor'}>{(unread_messages > 99 ? 99 : unread_messages )}</CountBallInline>) : '-'}
          </div>
        )
      }
    }

    if (getDataOfType(sessionData, 'organization.id', Number, null) === 1) {
      return [{
          ...chatColumns
        },
        {
        title: 'Организация',
        width: 200,
        dataIndex: 'organizationTitle',
        key: 'organizationTitle',
        sorter: true,
        sortOrder: orderFiltersRef.current.field === 'organizationTitle' && orderFiltersRef.current.order,
      }].concat(addDataColumns)
    }
    return addDataColumns.concat(chatColumns)
  }, [sessionData, orderFiltersRef.current])

  const cbTableRowClick = useCallback(({ id }) => navigate(`/case/${id}/edit`), [history])

  const deleteRecord = useCallback(async (currentId) => {
    try{
      await fetchAPI(`/api/collections/objects/${currentId}`, { method: 'DELETE'})
      setDataSource(dataSource => dataSource.filter(({ id }) => id !== currentId))
    }catch(error){
      console.error(error)
    }
  },[])

  const getData = useCallback(async (value, addUrlParams: object = {}) => {
    const urlParams = new URLSearchParams({...value, ...addUrlParams}).toString()

    navigate({
      search: urlParams
    })

    const pageErrors: string[] = []
    const [selectedSchema, metadata, collections, stages] = await Promise.all([
      fetchSchema({ name }),
      fetchAPI(`/api/collections/${name}/metadata`),
      fetchAPI(`/api/collections-case/${name}?access_key=axioma&${urlParams}&${addUrlParams}`),
      getStagesGroup('case')
    ])

    const collectionsResultData = getDataOfType(collections, 'data.data', Array, [])
    const workflowsResponse = await fetchAPI('/api/collections/objects-grouped/reason-for-cancellation?access_key=axioma')
    const workflows = getDataOfType(workflowsResponse, 'data.data', Array, [])
    function getWorkflowName(item){
      const result = workflows.find(wf => +wf.id === +item?.['workflow-id'])
      return result?.name
    }
    const ordersIds = collectionsResultData.map(item => item['id']).join(',')
    const nextStagesData = await fetchAPI(`/api/web-hooks/get-order-next-stages?ids=${ordersIds}`)
    const nextStages = getDataOfType(nextStagesData, 'data', Array, [])
    const fieldsOrderNamesData = await fetchAPI(`/api/web-hooks/get-fields-order-names?ids=${ordersIds}`)
    const fieldsOrderNames = getDataOfType(fieldsOrderNamesData, 'data', Array, [])

    const isMetadata = getDataOfType(metadata, 'data', Object, null)
    if(isMetadata === null) {
      pageErrors.push('Некорректный ответ сервера при получении метаданных коллекции')
    }

    const dataSource: any[] = formatListData(collectionsResultData, selectedSchema)
    const newDataSource = dataSource.map((item, index) => ({
      ...item,
      ...nextStages[index],
      ...fieldsOrderNames[index],
      deleteRecord,
      stages,
      'workflow-id': getWorkflowName(item),
    }))
    const totalRows = getDataOfType(collections, 'data.total-rows', Number, 0)
    setTotalRows(totalRows)
    setDataSource(newDataSource)
    setPageErrors(pageErrors)
    setIsDataLoading(false)
  }, [deleteRecord])

  const onOrderFilter = useCallback(async value => {
    orderFiltersRef.current = JSON.parse(JSON.stringify(value))
    setPage(1)
    setIsDataLoading(true)
    await getData(orderFiltersRef.current, {})
    setIsDataLoading(false)
  }, [getData])

  const handleCreateNewClick = useCallback(async () => {
    const result = await fetchAPI(`/api/web-hooks/create-case-from-sales-manager`, {
      method: 'POST'
    })
    const id = getDataOfType(result, 'data.id', Number, null)
    navigate(`/case/${id}/edit`)
  }, [history])

  const rowClassName = useCallback(record => {
    return record['stage'] === 0 ? 'table-row-dark' :  'table-row-light'
  }, [])

  const onPagination = useCallback(async ( page, pageSize) => {
    setPage(page)
    setIsDataLoading(true)

    const urlParams = page === 1 ? {} : {
      page: page,
      pageSize: pageSize,
    }

    await getData(orderFiltersRef.current, urlParams)
    setIsDataLoading(false)

  }, [getData])


  const onChangeSort = useCallback(async (pagination, filters, sorter) => {

    setPage(1)
    setIsDataLoading(true)

    if(sorter.order && typeof sorter.order !== 'undefined'){
      orderFiltersRef.current.field = sorter.field;
      orderFiltersRef.current.order = sorter.order;
    }else{
      delete orderFiltersRef.current.field;
      delete orderFiltersRef.current.order;
    }

    orderFiltersRef.current.page = page;
    orderFiltersRef.current.pageSize = 10;

    addDataColumns.forEach( col => {
      if( col.key === orderFiltersRef.current.field || col.dataIndex === orderFiltersRef.current.field){
        col.sortOrder = orderFiltersRef.current.order
      }else{
        delete col.sortOrder
      }
    })
    const newBeforeColumns = beforeDataColumns.map( item => {
      if(item.title === 'Стадия'){
        return Object.assign(item, {width: 165})
      }
      return item
    })
    // @ts-ignore
    const columns = newBeforeColumns.concat(addDataColumns, afterDataColumns)

    setColumns( columns )
    await getData(orderFiltersRef.current, {})
    setIsDataLoading(false)

  }, [getData, orderFiltersRef.current])

  useEffect(() => {
    const urlParams = Object.fromEntries(new URLSearchParams(location.search))

    if(urlParams.page) {
      setPage(parseInt(urlParams.page))
    }

    orderFiltersRef.current = Object.fromEntries(Object.entries(urlParams).filter(([key, value]) => key !== 'page' && key !== 'pageSize'))

    addDataColumns.forEach( col => {
      if( col.key === orderFiltersRef.current.field || col.dataIndex === orderFiltersRef.current.field){
        col.sortOrder = orderFiltersRef.current.order
      }
    })

    // @ts-ignore
    const columns = beforeDataColumns.concat(addDataColumns, afterDataColumns)

    getData(orderFiltersRef.current, urlParams)

    setColumns( columns )
    setTableWidth( calcTotalWidth(columns) )

  }, [counts, page, getData])

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      <TableHeaderWrapper>
        <Button onClick={handleCreateNewClick}>
          <PlusOutlined />
          Оформить обращение
        </Button>
        <OrderFilter onChange={onOrderFilter} />
      </TableHeaderWrapper>
      {(pageErrors.length > 0) ? (
        <div style={{ marginBottom: 40 }}>
          <Alert
            message="При выполнении операции возникла ошибка:"
            showIcon
            type="error"
            description={pageErrors.join('. ')}
            />
        </div>
      ): null}
      <TableWrapper>
        <Table
          showSorterTooltip={{overlay: "Нажмите для сортировки", placement: 'bottom'}}
          rowClassName={rowClassName}
          columns={columns}
          size="small"
          dataSource={dataSource}
          scroll={{ x: tableWidth, y: '72vh' }}
          pagination={false}
          loading={isDataLoading}
          onChange={onChangeSort}
          onHeaderRow={(column: any) => ({
            style: {
              fontSize: (column.key && column.key.substr(-3) === '-at') ? 9 : 13,
            }
          })}
          onRow={record => ({ onClick: event => tableRowClick(event, record, cbTableRowClick)})}
          rowKey='id'
          />
        </TableWrapper>
      {Boolean(totalRows) && <PaginationWrapper>
        <Pagination defaultCurrent={1} current={page} pageSize={10} total={totalRows} onChange={onPagination} showSizeChanger={false} />
      </PaginationWrapper>}
    </B2BLayout>
  )
}
